import type { NostrMetadata } from '@nostrify/nostrify';

/**
 * TrustWeb — Web of Trust scoring for Nostr identities.
 *
 * The score (0–100) is a composite of seven components computed from the
 * social graph (kind 3 contact lists), NIP-85 trusted assertions, and
 * profile metadata (kind 0). It is a heuristic of *connectedness and
 * vouched-for identity*, not a measure of character.
 */

/** Large indexing relays used for social-graph queries. */
export const WOT_INDEXER_RELAYS = [
  'wss://relay.nostr.band',
  'wss://relay.primal.net',
  'wss://nos.lol',
  'wss://relay.damus.io',
] as const;

/**
 * NIP-85 trusted assertion providers, in priority order. Each provider is
 * queried on its own relay; results are merged (first non-null tag wins).
 */
export const WOT_PROVIDERS = [
  {
    pubkey: '5f68e85ee174102ca8978eef302129f081f03456c884185d5ec1c1224ab633ea',
    relay: 'wss://relay.ditto.pub',
  },
  {
    pubkey: '4fd5e210530e4f6b2cb083795834bfe5108324f1ed9f00ab73b9e8fcfe5f12fe',
    relay: 'wss://nip85.nostr.band',
  },
] as const;

/** How many follower contact-list events to sample for graph analysis. */
export const FOLLOWER_SAMPLE_LIMIT = 150;
/** How many sampled followers get a second-degree (follower-of-follower) lookup. */
export const INFLUENCE_SAMPLE_LIMIT = 20;

/** Identity verification points (max 10). */
export const IDENTITY_POINTS = {
  nip05: 4,
  lightning: 2,
  picture: 1,
  banner: 1,
  about: 1,
  website: 1,
} as const;

export interface WotIdentityChecks {
  nip05: boolean;
  lightning: boolean;
  picture: boolean;
  banner: boolean;
  about: boolean;
  website: boolean;
}

export interface WotMetrics {
  /** Total followers (NIP-85). Null when no trusted assertion exists. */
  followers: number | null;
  /** Accounts the subject follows (from their kind 3). */
  following: number;
  /** Unique followers found in the graph sample. */
  sampledFollowers: number;
  /** Sampled followers the subject follows back. */
  mutualSampleCount: number;
  /** Mutual ratio within the sample (0–1). */
  mutualRatio: number;
  /** Estimated total mutual follows (extrapolated). Null when followers unknown. */
  estimatedMutuals: number | null;
  /** Median follower count of sampled followers (second-degree reach). */
  followerInfluence: number | null;
  replies: number;
  reactions: number;
  zapCount: number;
  zapAmount: number;
  reportsReceived: number;
  /** Posts published (NIP-85 post_cnt). */
  postCount: number;
  /** NIP-85 provider rank, normalized 0–100. */
  providerRank: number | null;
  /** Account age in days (from NIP-85 first_created_at). */
  accountAgeDays: number | null;
  /** True when any trusted assertion was found (even a sparse one). */
  hasAssertion: boolean;
  /** True when engagement numbers come from a 180-day sample, not provider totals. */
  engagementSampled: boolean;
  identity: WotIdentityChecks;
  /** True when the graph sample was too small to be reliable. */
  lowConfidence: boolean;
  /** True when stats come from direct sampling rather than NIP-85. */
  estimated: boolean;
}

export interface WotComponent {
  key: string;
  label: string;
  description: string;
  score: number;
  max: number;
}

export interface WotGrade {
  grade: string;
  label: string;
  blurb: string;
}

export interface WotResult {
  pubkey: string;
  metadata: NostrMetadata | undefined;
  metrics: WotMetrics;
  components: WotComponent[];
  total: number;
  grade: WotGrade;
  /** Sampled followers with the highest follower counts (second-degree). */
  notableFollowers: { pubkey: string; followers: number }[];
}

/** Log-scaled points: reaches `points` when `value` hits `anchor`. */
export function logScore(value: number, anchor: number, points: number): number {
  if (value <= 0) return 0;
  const scaled = Math.log10(1 + value) / Math.log10(1 + anchor);
  return Math.min(points, Math.round(scaled * points * 10) / 10);
}

export function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

export function identityChecks(metadata: NostrMetadata | undefined): WotIdentityChecks {
  return {
    nip05: Boolean(metadata?.nip05),
    lightning: Boolean(metadata?.lud16 || metadata?.lud06),
    picture: Boolean(metadata?.picture),
    banner: Boolean(metadata?.banner),
    about: Boolean(metadata?.about && metadata.about.trim().length > 0),
    website: Boolean(metadata?.website),
  };
}

export function identityScore(checks: WotIdentityChecks): number {
  return (Object.keys(IDENTITY_POINTS) as (keyof WotIdentityChecks)[]).reduce(
    (sum, key) => sum + (checks[key] ? IDENTITY_POINTS[key] : 0),
    0,
  );
}

export function gradeFor(score: number): WotGrade {
  if (score >= 85)
    return {
      grade: 'S',
      label: 'Pillar of the Web',
      blurb: 'A deeply vouched-for identity at the center of the Nostr social graph.',
    };
  if (score >= 70)
    return {
      grade: 'A',
      label: 'Highly Trusted',
      blurb: 'Strong, well-established trust signals across the network.',
    };
  if (score >= 55)
    return {
      grade: 'B',
      label: 'Well Connected',
      blurb: 'A solid web of trust with meaningful reach and engagement.',
    };
  if (score >= 40)
    return {
      grade: 'C',
      label: 'Established',
      blurb: 'Visible trust anchors, with room to grow deeper connections.',
    };
  if (score >= 28)
    return {
      grade: 'D',
      label: 'Emerging',
      blurb: 'Early trust signals are present but the web is still thin.',
    };
  if (score >= 15)
    return {
      grade: 'E',
      label: 'Sprouting',
      blurb: 'Only a handful of connections vouch for this identity so far.',
    };
  return {
    grade: 'F',
    label: 'Isolated',
    blurb: 'Little to no web-of-trust signal. New, dormant, or disconnected.',
  };
}

/** Compute the composite score from gathered metrics. Pure and testable. */
export function computeScore(
  metrics: WotMetrics,
): { components: WotComponent[]; total: number; grade: WotGrade } {
  const components: WotComponent[] = [];

  // 1. Network Reach (25) — total followers, log-scaled to ~5k.
  components.push({
    key: 'reach',
    label: 'Network Reach',
    description:
      metrics.followers !== null
        ? `${metrics.followers.toLocaleString()} followers across the network${metrics.estimated ? ' (sampled estimate)' : ''}.`
        : 'Follower count unavailable — no trusted assertion found.',
    score: metrics.followers !== null ? logScore(metrics.followers, 5000, 25) : 0,
    max: 25,
  });

  // 2. Mutual Trust (15) — share of sampled followers the subject follows back.
  components.push({
    key: 'mutual',
    label: 'Mutual Trust',
    description:
      metrics.sampledFollowers > 0
        ? `${Math.round(metrics.mutualRatio * 100)}% of sampled followers are followed back${metrics.estimatedMutuals !== null ? ` (~${metrics.estimatedMutuals.toLocaleString()} mutuals total)` : ''}.`
        : 'No follower overlap could be sampled.',
    score: Math.min(15, Math.round(metrics.mutualRatio * 30 * 10) / 10),
    max: 15,
  });

  // 3. Follower Influence (15) — median reach of the subject's followers.
  components.push({
    key: 'influence',
    label: 'Follower Influence',
    description:
      metrics.followerInfluence !== null
        ? `Typical follower of this account is followed by ~${Math.round(metrics.followerInfluence).toLocaleString()} others.`
        : 'Second-degree reach could not be measured.',
    score: metrics.followerInfluence !== null ? logScore(metrics.followerInfluence, 1000, 15) : 0,
    max: 15,
  });

  // 4. Engagement (20) — weighted interactions.
  const weighted =
    metrics.reactions + metrics.replies * 2 + metrics.zapCount * 3;
  const engagementSuffix = metrics.engagementSampled
    ? ' in the last 180 days (sampled)'
    : ' received (lifetime, via NIP-85)';
  components.push({
    key: 'engagement',
    label: 'Engagement',
    description: `${metrics.reactions.toLocaleString()} reactions, ${metrics.replies.toLocaleString()} replies, and ${metrics.zapCount.toLocaleString()} zaps (${metrics.zapAmount.toLocaleString()} sats)${engagementSuffix}.`,
    score: logScore(weighted, 20000, 20),
    max: 20,
  });

  // 5. Identity & Verification (10).
  const idScore = identityScore(metrics.identity);
  const passed = Object.entries(metrics.identity)
    .filter(([, v]) => v)
    .map(([k]) => k);
  components.push({
    key: 'identity',
    label: 'Identity & Verification',
    description:
      passed.length > 0
        ? `Verified signals: ${passed.join(', ')}.`
        : 'No verification signals (NIP-05, lightning address, profile) found.',
    score: idScore,
    max: 10,
  });

  // 6. Longevity (10) — account age, maxes at 3 years. When no first-seen
  // date exists but the account is clearly active, award partial credit.
  let longevityScore = 0;
  let longevityDesc = 'Account age unknown.';
  if (metrics.accountAgeDays !== null) {
    longevityScore = Math.min(
      10,
      Math.round((metrics.accountAgeDays / 1095) * 10 * 10) / 10,
    );
    longevityDesc = `First seen ${(metrics.accountAgeDays / 365.25).toFixed(1)} years ago.`;
  } else if (metrics.postCount > 50 || (metrics.followers ?? 0) > 100) {
    longevityScore = 4;
    longevityDesc =
      'First-seen date unavailable — partial credit for substantial observed activity.';
  }
  components.push({
    key: 'longevity',
    label: 'Longevity',
    description: longevityDesc,
    score: longevityScore,
    max: 10,
  });

  // 7. Provider Rank (5) — external NIP-85 web-of-trust rank.
  components.push({
    key: 'rank',
    label: 'External WoT Rank',
    description:
      metrics.providerRank !== null
        ? `Independent NIP-85 trust provider ranks this identity ${metrics.providerRank}/100.`
        : metrics.hasAssertion
          ? 'Trusted assertion exists, but the provider published no rank.'
          : 'No external trust assertion available for this identity.',
    score:
      metrics.providerRank !== null
        ? Math.round((Math.min(100, Math.max(0, metrics.providerRank)) / 100) * 5 * 10) / 10
        : 0,
    max: 5,
  });

  // Negative signal: reports filed against the identity.
  const penalty = Math.min(10, metrics.reportsReceived * 2);

  const raw = components.reduce((sum, c) => sum + c.score, 0) - penalty;
  const total = Math.max(0, Math.min(100, Math.round(raw)));

  return { components, total, grade: gradeFor(total) };
}
